import axios, { AxiosRequestConfig } from "axios";
import { ProformaManagementDef } from "@/model/component.model";
import {
  CreateOrUpdateShipmentDTO,
  ProformaDTO,
  ShipmentDTO
} from "@/model/data.model";
import store from "../vuex/store";
import { ShipmentService } from "./ShipmentService";
import _ from "lodash";

export default class ProformaService {
  getProforma(shipmentId: string) {
    const blankProforma = {
      invoiceNumber: 0,
      invoiceDate: "",
      declarationText: "",
      additionalComment: "",
      currency: "EUR",
      eori: "",
      totalValue: 0,
      proformaDetails: [],
      incoterms: "",
      vatNumber: "",
      fiscalCode: "",
      senderCompanyName: "",
      senderAddress: "",
      senderCity: "",
      senderProvince: "",
      senderZipCode: "",
      senderCountryName: "",
      senderPhone: "",
      receiverVatNumber: "",
      receiverEori: "",
      receiverFiscalCode: "",
      receiverCompanyName: "",
      receiverSecondAddress: "",
      receiverAddress: "",
      receiverCity: "",
      receiverProvince: "",
      receiverZipCode: "",
      receiverCountryName: "",
      receiverPhone: "",
      proformaSenderType: "",
      proformaReceiverType: "",
      mrnNumber: ""
    } as ProformaManagementDef;

    if (shipmentId) {
      return axios
        .get(
          store.getters.BASE_API_URL + "/shipments/" + shipmentId + "/proforma"
        )
        .then(function(res) {
          const proformaDto = res.data as ProformaDTO;

          const proforma = {
            invoiceNumber: proformaDto.invoiceNumber,
            invoiceDate: proformaDto.invoiceDate,
            declarationText: proformaDto.declarationText,
            additionalComment: proformaDto.additionalComment,
            currency: proformaDto.currency,
            eori: proformaDto.eori,
            totalValue: proformaDto.totalValue,
            proformaDetails: proformaDto.proformaDetails,
            incoterms: proformaDto.incoterms,
            vatNumber: proformaDto.vatNumber,
            fiscalCode: proformaDto.fiscalCode,
            receiverVatNumber: proformaDto.receiverVatNumber,
            receiverFiscalCode: proformaDto.receiverFiscalCode,
            receiverEori: proformaDto.receiverEori,
            senderCompanyName: proformaDto.senderCompanyName,
            senderAddress: proformaDto.senderAddress,
            senderCity: proformaDto.senderCity,
            senderProvince: proformaDto.senderProvince,
            senderZipCode: proformaDto.senderZipCode,
            senderCountryName: proformaDto.senderCountryName,
            senderPhone: proformaDto.senderPhone,
            invoiceType: proformaDto.invoiceType,
            mbeReasonCode: proformaDto.mbeReasonCode,
            receiverCompanyName: proformaDto.receiverCompanyName,
            receiverSecondAddress: proformaDto.receiverSecondAddress,
            receiverAddress: proformaDto.receiverAddress,
            receiverCity: proformaDto.receiverCity,
            receiverProvince: proformaDto.receiverProvince,
            receiverZipCode: proformaDto.receiverZipCode,
            receiverCountryName: proformaDto.receiverCountryName,
            receiverPhone: proformaDto.receiverPhone,
            proformaSenderType: proformaDto.proformaSenderType,
            proformaReceiverType: proformaDto.proformaReceiverType,
            mrnNumber: proformaDto.mrnNumber,
            declarationStatement: proformaDto.declarationStatement,
            vatStatus: proformaDto.vatStatus
          } as ProformaManagementDef;
          return proforma;
        })
        .catch(err => {
          if (err.response.status === 404) {
            const proforma = blankProforma;
            return Promise.resolve(proforma);
          } else {
            console.error("Response failed with message: " + err.message);
            return Promise.reject(err);
          }
        });
    } else {
      const proforma = blankProforma;
      return Promise.resolve(proforma);
    }
  }

  saveProforma(
    shipmentId: string,
    proforma: ProformaDTO
  ): Promise<ProformaDTO> {
    return this.postProforma(shipmentId, proforma);
  }

  postProforma(
    shipmentId: string,
    proforma: ProformaDTO
  ): Promise<ProformaDTO> {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: proforma,
      url: store.getters.BASE_API_URL + "/shipments/" + shipmentId + "/proforma"
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }
  printProforma(shipmentId: string) {
    const config: AxiosRequestConfig = {
      url: store.getters.BASE_API_URL + "/shipments/proforma/print",
      method: "GET",
      params: {
        shipmentId,
        pdfLanguage: store.getters.language
      }
    };

    return axios
      .request(config)
      .then(res => {
        return res.data;
      })
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  previewProforma(
    shipment: ShipmentDTO,
    tenantId: number,
    multiStoreId: number,
    pdfLanguage?: string
  ): Promise<string> {
    let body: CreateOrUpdateShipmentDTO = {
      messageResult: {
        messages: []
      },
      shipment: _.cloneDeep(shipment)
    };

    body = ShipmentService.prepareCreateOrUpdateShipmentDTO(
      body,
      tenantId,
      multiStoreId,
      false,
      false
    );

    const config: AxiosRequestConfig = {
      url: store.getters.BASE_API_URL + "/shipments/proforma/preview",
      method: "POST",
      data: body.shipment,
      params: {
        pdfLanguage: pdfLanguage
      }
    };
    return axios
      .request(config)
      .then(res => {
        const data = res.data;
        return data;
      })
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  generateDualUseDocument(
    shipment: ShipmentDTO,
    tenantId: number,
    multiStoreId: number,
    language: string
  ): Promise<string> {
    let body: CreateOrUpdateShipmentDTO = {
      messageResult: {
        messages: []
      },
      shipment: _.cloneDeep(shipment)
    };

    body = ShipmentService.prepareCreateOrUpdateShipmentDTO(
      body,
      tenantId,
      multiStoreId,
      false,
      false
    );

    const config: AxiosRequestConfig = {
      url: store.getters.BASE_API_URL + "/shipments/dualuse",
      method: "POST",
      data: body.shipment,
      params: {
        language: language
      },
      timeout: 28000
    };
    return axios
      .request(config)
      .then(res => res.data)
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }
}
